import { type CodigoErro, ehCodigoErro, mensagemDeErro } from '@entregas/core';

/** Erro que as telas sabem mostrar: tem código estável e mensagem em pt-BR. */
export class ErroApi extends Error {
  readonly codigo: CodigoErro;
  readonly causa: unknown;

  constructor(codigo: CodigoErro, causa?: unknown) {
    super(mensagemDeErro(codigo));
    this.name = 'ErroApi';
    this.codigo = codigo;
    this.causa = causa;
  }
}

interface ErroComCodigo {
  code?: string;
  message?: string;
}

// O fetch sem rede em cada ambiente: Chrome, polyfill do React Native (falha e tempo esgotado), o
// expo/fetch que os apps usam de fato no Expo 58 ("fetch failed: ..."), Node, Firefox e Safari.
const FALHA_DE_REDE =
  /Failed to fetch|Network request (failed|timed out)|fetch failed|NetworkError|Load failed/i;

/**
 * O fetch falhou antes de o servidor responder? O postgrest-js não lança: devolve o objeto
 * `{ message: 'TypeError: Network request failed', code: '' }`. Erro do banco sempre tem código,
 * então só o objeto sem código e com a mensagem do fetch é rede. Só o "TypeError:" na frente não
 * basta: o postgrest-js também captura erro de programação (JSON circular no corpo), e esse não
 * pode virar "confira a internet" nem ser repetido.
 */
function ehFalhaDeRede(erro: unknown, { code, message = '' }: ErroComCodigo): boolean {
  if (erro instanceof TypeError) return FALHA_DE_REDE.test(erro.message);
  return !code && FALHA_DE_REDE.test(message);
}

/**
 * Converte qualquer erro do Supabase/rede em ErroApi.
 * - `raise exception 'codigo_invalido'` no banco → codigo_invalido
 * - `transicao_invalida: pendente -> entregue` → transicao_invalida
 * - violação de unicidade em clientes(telefone) → telefone_duplicado
 * - RLS ou grant (42501) → sem_permissao
 * - fetch sem rede (lançado ou devolvido pelo postgrest-js) → sem_conexao
 */
export function traduzirErro(erro: unknown): ErroApi {
  if (erro instanceof ErroApi) return erro;

  const campos = (erro ?? {}) as ErroComCodigo;
  if (ehFalhaDeRede(erro, campos)) return new ErroApi('sem_conexao', erro);

  const { code, message = '' } = campos;
  if (code === '23505' && /telefone/.test(message)) return new ErroApi('telefone_duplicado', erro);
  if (code === '23505' && /produtos_nome_unico/.test(message)) {
    return new ErroApi('produto_repetido', erro);
  }
  // Checks da tabela de produtos (nome, preço, ordem, quantos apelidos): o form já valida, é a rede de segurança.
  if (code === '23514' && /produtos_/.test(message)) return new ErroApi('produto_invalido', erro);
  // Check da logo da loja (formato, tamanho, bytes do arquivo): a tela já reduz e valida antes.
  if (code === '23514' && /empresas_logo/.test(message)) return new ErroApi('logo_invalida', erro);
  // RLS recusou a escrita (insert fora das lojas do usuário, ou o balcão onde só o dono pode) ou falta grant.
  if (code === '42501') return new ErroApi('sem_permissao', erro);

  const prefixo = message.split(':')[0]?.trim() ?? '';
  if (ehCodigoErro(prefixo)) return new ErroApi(prefixo, erro);

  if (/JWT|not authenticated|Auth session missing/i.test(message)) {
    return new ErroApi('nao_autenticado', erro);
  }
  return new ErroApi('desconhecido', erro);
}

// Tipados pela resposta inteira (e não pelo `data`): as respostas do supabase-js são uniões
// { data: Linha; error: null } | { data: null; error: Erro }, e inferir só o `data` perde a Linha.
interface Resposta {
  data: unknown;
  error: unknown;
}

/** Desembrulha a resposta do supabase-js: devolve `data` (nunca nulo) ou lança ErroApi. */
export function exigir<R extends Resposta>(resposta: R): NonNullable<R['data']> {
  if (resposta.error) throw traduzirErro(resposta.error);
  if (resposta.data == null) throw new ErroApi('desconhecido', 'resposta sem dados');
  return resposta.data as NonNullable<R['data']>;
}

/** Para consultas que podem não achar nada (`maybeSingle`, função que devolve nulo). */
export function exigirOuNulo<R extends Resposta>(resposta: R): NonNullable<R['data']> | null {
  if (resposta.error) throw traduzirErro(resposta.error);
  return (resposta.data ?? null) as NonNullable<R['data']> | null;
}

/** Para escritas sem retorno: só confere o erro. */
export function exigirSucesso(resposta: { error: unknown }): void {
  if (resposta.error) throw traduzirErro(resposta.error);
}
